import React, {
  useState,
  useEffect,
  useMemo,
  useCallback
} from "react";
import axios from "axios";
import { fetchToken } from "./Auth";
import "../css/modalDocs.css";

/* =========================
   ÍCONE DO ARQUIVO
========================= */
const FileIcon = ({ mimeType }) => {
  let iconClass = "fas fa-file";
  let color = "#6c757d";

  if (typeof mimeType === "string") {
    if (mimeType.includes("pdf")) {
      iconClass = "fas fa-file-pdf";
      color = "#ff4757";
    } else if (
      mimeType.includes("word") ||
      mimeType.includes("officedocument")
    ) {
      iconClass = "fas fa-file-word";
      color = "#3742fa";
    } else if (
      mimeType.includes("excel") ||
      mimeType.includes("spreadsheet")
    ) {
      iconClass = "fas fa-file-excel";
      color = "#2ed573";
    } else if (mimeType.includes("image")) {
      iconClass = "fas fa-file-image";
      color = "#a55eea";
    }
  }

  return <i className={`${iconClass} fa-2x`} style={{ color }} />;
};

/* =========================
   MODAL DOCUMENTOS
========================= */
function ModalDocumentos({ projetoId, onClose }) {
  const [documentos, setDocumentos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [etapaUpload, setEtapaUpload] = useState("1");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  const apiBaseUrl = "/api";
  const token = fetchToken();

  const etapasDisponiveis = [
    { id: "1", label: "Etapa 1 - Planejamento" },
    { id: "2", label: "Etapa 2 - Execução" },
    { id: "3", label: "Etapa 3 - Monitoramento" },
    { id: "4", label: "Etapa 4 - Medição" },
    { id: "5", label: "Etapa 5 - Pagamento" },
    { id: "6", label: "Etapa 6 - Conclusão" },
    { id: "0", label: "Geral / Outros" }
  ];

  /* =========================
     BUSCAR DOCUMENTOS
  ========================= */
  const fetchDocumentos = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${apiBaseUrl}/projetocompleto/${projetoId}/documentos`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const resultado = res.data?.Result;
      setDocumentos(Array.isArray(resultado) ? resultado : []);
      setError(null);
    } catch (err) {
      setDocumentos([]);
      setError("Erro ao carregar documentos.");
    } finally {
      setIsLoading(false);
    }
  }, [projetoId, token]);

  useEffect(() => {
    fetchDocumentos();
  }, [fetchDocumentos]);

  /* =========================
     DOCUMENTOS FILTRADOS
  ========================= */
  const documentosFiltrados = useMemo(() => {
    return Array.isArray(documentos)
      ? documentos.filter(
          (doc) => String(doc?.etapa) === activeTab
        )
      : [];
  }, [documentos, activeTab]);

  /* =========================
     UPLOAD
  ========================= */
  const handleFileChange = (e) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    const formData = new FormData();
    selectedFiles.forEach((file) =>
      formData.append("files", file)
    );
    formData.append("etapa", etapaUpload);

    try {
      await axios.post(
        `${apiBaseUrl}/projetocompleto/${projetoId}/upload-documento`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setSelectedFiles([]);
      document.getElementById("fileInputCustom").value = "";
      await fetchDocumentos();
      setActiveTab(etapaUpload);
    } catch (err) {
      setUploadError("Erro ao enviar documentos.");
    } finally {
      setIsUploading(false);
    }
  };

  /* =========================
     GERAR DOSSIÊ
  ========================= */
  const handleGerarDossie = async () => {
    if (!Array.isArray(documentos) || documentos.length === 0) {
      alert("Sem documentos.");
      return;
    }

    setIsGeneratingPdf(true);

    try {
      const res = await axios.get(
        `${apiBaseUrl}/projetocompleto/${projetoId}/gerar-dossie`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([res.data])
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Dossie_Projeto_${projetoId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Erro ao gerar PDF.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  /* =========================
     DOWNLOAD / DELETE
  ========================= */
  const handleDownload = async (id, nome) => {
    try {
      const res = await axios.get(
        `${apiBaseUrl}/projetocompleto/documentos/${id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([res.data])
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      alert("Erro ao baixar.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir documento?")) return;

    try {
      await axios.delete(
        `${apiBaseUrl}/projetocompleto/documentos/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchDocumentos();
    } catch {
      alert("Erro ao excluir.");
    }
  };

  /* =========================
     RENDER
  ========================= */
  return (
    <div className="modal-backdrop-custom" onClick={onClose}>
      <div
        className="modal-content-custom"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header-custom">
          <h5>
            <i className="fas fa-folder-open text-primary me-2" />
            Gestão de Documentos
          </h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        <div className="modal-body-custom">
          {/* UPLOAD */}
          <div className="upload-box mb-4">
            <h6 className="fw-bold">
              <i className="fas fa-cloud-upload-alt me-2" />
              Novo Upload
            </h6>

            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="small fw-bold">Etapa:</label>
                <select
                  className="form-select form-select-sm"
                  value={etapaUpload}
                  onChange={(e) =>
                    setEtapaUpload(e.target.value)
                  }
                >
                  {etapasDisponiveis.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="small fw-bold">Arquivos:</label>
                <input
                  type="file"
                  className="form-control form-control-sm"
                  id="fileInputCustom"
                  multiple
                  onChange={handleFileChange}
                />
              </div>

              <div className="col-md-2">
                <button
                  className="btn btn-primary btn-sm w-100"
                  onClick={handleUpload}
                  disabled={
                    isUploading || selectedFiles.length === 0
                  }
                >
                  {isUploading ? "..." : "Enviar"}
                </button>
              </div>
            </div>

            {uploadError && (
              <div className="text-danger small mt-2">
                {uploadError}
              </div>
            )}
          </div>

          {/* TABS */}
          <ul className="nav nav-tabs mb-3">
            {etapasDisponiveis.map((etapa) => {
              const count = Array.isArray(documentos)
                ? documentos.filter(
                    (d) => String(d?.etapa) === etapa.id
                  ).length
                : 0;

              return (
                <li className="nav-item" key={etapa.id}>
                  <button
                    className={`nav-link ${
                      activeTab === etapa.id
                        ? "active fw-bold"
                        : ""
                    }`}
                    onClick={() =>
                      setActiveTab(etapa.id)
                    }
                  >
                    {etapa.label.split(" - ")[0]}
                    {count > 0 && (
                      <span className="badge bg-secondary ms-1">
                        {count}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* LISTA */}
          <div className="tab-content-area">
            <h6 className="text-primary border-bottom pb-2 mb-3">
              {
                etapasDisponiveis.find(
                  (e) => e.id === activeTab
                )?.label
              }
            </h6>

            {isLoading ? (
              <div className="text-center">Carregando...</div>
            ) : documentosFiltrados.length === 0 ? (
              <div className="text-center text-muted">
                Vazio
              </div>
            ) : (
              <div className="doc-list">
                {documentosFiltrados.map((doc) => (
                  <div key={doc.id} className="doc-item">
                    <div className="doc-info">
                      <FileIcon mimeType={doc.tipo_mime} />
                      <div className="ms-2">
                        <span
                          className="doc-name"
                          onClick={() =>
                            handleDownload(
                              doc.id,
                              doc.nome_original
                            )
                          }
                        >
                          {doc.nome_original}
                        </span>
                        <small className="text-muted">
                          {doc.data_upload}
                        </small>
                      </div>
                    </div>
                    <button
                      className="btn btn-link text-danger"
                      onClick={() =>
                        handleDelete(doc.id)
                      }
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer-custom d-flex justify-content-between">
          <button
            className="btn btn-success fw-bold"
            onClick={handleGerarDossie}
            disabled={
              isGeneratingPdf ||
              !Array.isArray(documentos) ||
              documentos.length === 0
            }
          >
            {isGeneratingPdf
              ? "Gerando PDF..."
              : "Baixar Dossiê Completo"}
          </button>

          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalDocumentos;
